<template>
  <ViewerLayoutRoot class="video-owner-layout" data-testid="video-owner-layout">
    <VideoOwnerTopBar />

    <!-- Split view: Logs mode or desktop Video mode -->
    <template v-if="splitConfig">
      <SplitViewContainer class="video-owner-layout__split" v-bind="splitConfig">
        <template #left>
          <div class="video-owner-layout__video-col">
            <div class="video-owner-layout__video">
              <ViewerContent />
            </div>
            <MetadataSection />
          </div>
        </template>
        <template #right>
          <div class="video-owner-layout__side-panel">
            <component :is="rightPanel" />
          </div>
        </template>
      </SplitViewContainer>
    </template>

    <!-- Stacked: mobile -->
    <div v-else class="video-owner-layout__media-row" :class="{ 'video-owner-layout__media-row--single': !rightPanel }">
      <div class="video-owner-layout__video-col">
        <div class="video-owner-layout__video">
          <ViewerContent />
        </div>
        <div v-if="isSmallMobile" class="mt-3 px-4">
          <ItemName />
        </div>
        <div class="px-4 sm:px-0">
          <MetadataSection />
        </div>
      </div>
      <div v-if="rightPanel" class="video-owner-layout__side-panel px-4 sm:px-0">
        <component :is="rightPanel" />
      </div>
    </div>

    <!-- Content Tabs -->
    <div class="video-owner-layout__tabs px-4 sm:px-0">
      <VideoOwnerTabs @addedComment="emit('addedComment')" />
    </div>
  </ViewerLayoutRoot>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, type Component } from 'vue';
import { storeToRefs } from 'pinia';

import ViewerContent from '@/modules/viewer/components/Content.vue';
import ItemName from '@/modules/viewer/components/Name.vue';
import SmartActionsSidePanel from '@/modules/viewer/components/SmartActionsSidePanel.vue';
import SmartActionsUpsell from '@/modules/viewer/components/SmartActionsUpsell.vue';
import MeetingOwnerSidePanel from '@/modules/viewer/components/meeting/MeetingOwnerSidePanel.vue';
import LogsSidePanel from '@/modules/viewer/components/logs/LogsSidePanel.vue';
import SplitViewContainer from '@/modules/viewer/components/SplitViewContainer.vue';
import VideoOwnerTopBar from './VideoOwnerTopBar.vue';
import VideoOwnerTabs from './VideoOwnerTabs.vue';
import MetadataSection from '../../shared/MetadataSection.vue';

import { useVideoAi } from '@/modules/viewer/composables/useVideoAi';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useViewerModeStore } from '@/modules/viewer/store/useViewerModeStore';
import { useViewerAvailableModes } from '@/modules/viewer/composables/useViewerAvailableModes';
import { useViewerModeUrlSync } from '@/modules/viewer/composables/useViewerModeUrlSync';
import { useSmartActionsNoVoice } from '@/modules/viewer/composables/useSmartActionsNoVoice';
import { useMobileDetection } from '@/composables/useMobileDetection';

import ViewerLayoutRoot from '../../shared/ViewerLayoutRoot.vue';
const emit = defineEmits<{
  addedComment: [];
}>();

const { hasAiAddon } = useVideoAi();
const { isMeeting, meetingInfo } = storeToRefs(useViewerItemStore());
const { isLogsMode } = storeToRefs(useViewerModeStore());

const { isMobile } = useViewerAvailableModes();
const { isMobile: isSmallMobile } = useMobileDetection();
const isDesktop = computed(() => !isMobile.value);
const { hasTerminalNoVoice } = useSmartActionsNoVoice();

useViewerModeUrlSync();

const splitConfig = computed(() => {
  if (isLogsMode.value) {
    return { defaultRatio: 0.5, minRatio: 0.25, maxRatio: 0.75, storageKey: 'viewer-logs-split-ratio' };
  }
  if (isDesktop.value && rightPanel.value) {
    return { defaultRatio: 0.75, minRatio: 0.4, maxRatio: 0.85, storageKey: 'viewer-video-split-ratio' };
  }
  return null;
});

const rightPanel = computed<Component | null>(() => {
  if (isLogsMode.value) return LogsSidePanel;
  if (isMeeting.value && !!meetingInfo.value) return MeetingOwnerSidePanel;
  if (hasTerminalNoVoice.value) return null;
  if (hasAiAddon.value) return SmartActionsSidePanel;
  return SmartActionsUpsell;
});

// Reserve scrollbar gutter on the document while the owner video layout is
// mounted. Without this, clicking a bottom tab (Summary → Transcript, etc.)
// that changes the tabs content height can cause the viewport scrollbar to
// appear/disappear, which shifts the layout by ~15px. Percentage-based
// widths in the split view cascade that change into the video's aspect-ratio
// height, producing a visible "dance" of the video and logs panels.
onMounted(() => {
  document.documentElement.classList.add('viewer-stable-scrollbar');
});

onUnmounted(() => {
  document.documentElement.classList.remove('viewer-stable-scrollbar');
});
</script>

<style scoped>
.video-owner-layout__media-row {
  @apply flex flex-col gap-4;
}

@media (min-width: 1024px) {
  .video-owner-layout__media-row {
    display: grid;
    grid-template-columns: 1fr minmax(280px, 360px);
    align-items: stretch;
    gap: 1rem;
  }

  .video-owner-layout__media-row--single {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 1440px) {
  .video-owner-layout__media-row {
    grid-template-columns: 1fr 360px;
  }

  .video-owner-layout__media-row--single {
    grid-template-columns: 1fr;
  }
}

.video-owner-layout__video-col {
  @apply flex flex-col min-w-0;
}

.video-owner-layout__video {
  @apply min-w-0;
}

.video-owner-layout__video :deep(.content-wrapper) {
  background-color: transparent !important;
}

/* Mobile: side panel below video, collapsible */
.video-owner-layout__side-panel {
  @apply flex flex-col w-full mt-2;
}

/* Desktop: side column */
@media (min-width: 1024px) {
  .video-owner-layout__side-panel {
    @apply mt-0;
  }

  .video-owner-layout__side-panel :deep(.smart-actions-panel),
  .video-owner-layout__side-panel :deep(.meeting-owner-panel) {
    @apply flex-grow;
    max-height: 100%;
    overflow-y: auto;
  }
}

.video-owner-layout__tabs {
  @apply mt-2;
}

/* Split view (Logs mode) */
.video-owner-layout__split {
  min-height: 0;
}

.video-owner-layout__split :deep(.video-owner-layout__side-panel) {
  @apply mt-0 h-full;
}

.video-owner-layout__split :deep(.logs-panel) {
  @apply h-full;
}

.video-owner-layout__split :deep(.video-owner-layout__video-col) {
  @apply h-full;
}
</style>
